'use client';

import React, { useState, useEffect, useRef } from 'react';

interface LayerItem {
  num: string;
  title: string;
  subtitle: string;
  items: string[];
}

const layers: LayerItem[] = [
  {
    num: '01',
    title: 'Business Planning Architecture',
    subtitle: 'Define the planning strategy and operating model before touching SAC.',
    items: [
      'Define planning strategy and corporate planning model',
      'Connected planning across business units',
      'Planning governance framework and RACI',
      'Planning calendars and cycle design',
      'Stakeholder alignment and change management approach',
    ],
  },
  {
    num: '02',
    title: 'Planning Data Architecture',
    subtitle: 'Design the data structures that will carry the plan.',
    items: [
      'Planning model types — public vs private dimensions',
      'Dimension design and hierarchy management',
      'Master data governance and ownership',
      'Version management strategy (Actual, Budget, Forecast, Simulation)',
      'Currency translation and handling',
      'Time structures — fiscal vs calendar year',
    ],
  },
  {
    num: '03',
    title: 'Data Integration Layer',
    subtitle: 'Bring actuals and reference data into the planning environment reliably.',
    items: [
      'S/4HANA live connection and data import strategy',
      'SAP Datasphere integration architecture',
      'Flat file integration for non-SAP sources',
      'API integrations for third-party data',
      'Data pipeline design and scheduling',
      'Data quality validation and exception handling',
    ],
  },
  {
    num: '04',
    title: 'Planning Model Design',
    subtitle: 'Build the right model structures for planning accuracy and performance.',
    items: [
      'Fact model design — planning vs reporting models',
      'Dimension model design and normalisation',
      'Star schema design principles for SAC',
      'Measure design — restricted, calculated, formula',
      'Driver model architecture',
      'Allocation logic and cost distribution design',
    ],
  },
  {
    num: '05',
    title: 'Planning Logic Layer',
    subtitle: 'Implement the calculation engine that powers the plan.',
    items: [
      'Data actions design and best practices',
      'Advanced formula scripting (script logic)',
      'Allocation engines — top-down, bottom-up, proportional',
      'Driver-based planning formulas',
      'Simulation logic and what-if scenario design',
      'Scenario modelling architecture',
    ],
  },
  {
    num: '06',
    title: 'Planning Experience Layer',
    subtitle: 'Design the interface through which planners interact with the model.',
    items: [
      'Planning stories — input table design',
      'Planning input forms and data entry UX',
      'Variance dashboards — Actual vs Plan vs Forecast',
      'Workflow task configuration and routing',
      'Collaboration tools and commenting',
      'Approval processes and sign-off workflows',
    ],
  },
  {
    num: '07',
    title: 'Planning Governance & Operations',
    subtitle: 'Sustain the planning program through structured operations.',
    items: [
      'Planning cycles and calendar management',
      'Forecast cadence and rolling forecast operations',
      'Access controls and security role design',
      'Auditability and version locking',
      'Performance optimisation and model maintenance',
      'Planning Centre of Excellence setup',
    ],
  },
];

export default function SevenLayers() {
  const [activeLayer, setActiveLayer] = useState<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.03 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const active = layers[activeLayer];

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border">
      <div className="max-w-container mx-auto">
        <div className="mono-label mb-8 reveal-on-scroll">Architecture Layers</div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Layer nav */}
          <div className="md:col-span-4 reveal-on-scroll">
            <div className="border border-border" style={{ borderRadius: '4px', overflow: 'hidden' }}>
              {layers.map((layer, i) => (
                <button
                  key={layer.num}
                  onClick={() => setActiveLayer(i)}
                  className={`w-full text-left px-5 py-4 border-b border-border last:border-0 transition-colors duration-150 ${
                    activeLayer === i
                      ? 'bg-primary text-white' :'bg-white text-foreground hover:bg-secondary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="font-mono text-xs shrink-0"
                      style={{ color: activeLayer === i ? 'rgba(255,255,255,0.5)' : 'var(--color-muted)' }}
                    >
                      {layer.num}
                    </span>
                    <span className="font-body font-medium text-sm leading-snug">
                      {layer.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Layer detail */}
          <div className="md:col-span-8 reveal-on-scroll reveal-delay-2">
            <div className="border border-border p-8 bg-white h-full" style={{ borderRadius: '4px' }}>
              <div className="mono-label mb-4">Layer {active.num}</div>
              <h3
                className="font-heading font-semibold text-foreground mb-3"
                style={{ fontSize: '1.5rem', letterSpacing: '-0.02em' }}
              >
                {active.title}
              </h3>
              <p className="text-sm text-muted mb-6 leading-relaxed font-body">
                {active.subtitle}
              </p>
              <div className="border-t border-border pt-6">
                <div className="mono-label mb-4">Key Design Considerations</div>
                <ul className="flex flex-col gap-3">
                  {active.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm font-body text-foreground">
                      <span className="text-muted mt-0.5 shrink-0">—</span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}